import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ProductsProvider } from "./context/ProductsContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import EkmanLayout from "./components/ekman/Layout";
import { Toaster } from "./components/ui/sonner";

// Lazy load pages for faster initial load
const HomePage = lazy(() => import("./pages/HomePage"));
const ShopPage = lazy(() => import("./pages/ShopPage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const StoryPage = lazy(() => import("./pages/StoryPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const TinyTreasuresPage = lazy(() => import("./pages/TinyTreasuresPage"));
const MyOrdersPage = lazy(() => import("./pages/MyOrdersPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentFailurePage = lazy(() => import("./pages/PaymentFailurePage"));
const ProtectedRoute = lazy(() => import("./components/auth/ProtectedRoute"));
const AuthCallback = lazy(() => import("./components/auth/AuthCallback"));

const PageLoader = () => (
  <div className="flex items-center justify-center py-20">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-black border-t-transparent"></div>
  </div>
);

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  if (location.pathname === "/auth/callback") {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<EkmanLayout />}>
            <Route path="auth/callback" element={<AuthCallback />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<EkmanLayout />}>
          <Route index element={<HomePage />} />
          <Route path="shop-all" element={<ShopPage />} />
          <Route path="bath-salts" element={<CategoryPage title="Bath Salts" filterType="section" filterValue="Bath Salts" />} />
          <Route path="body-oils" element={<CategoryPage title="Body Oils" filterType="section" filterValue="Body & Face Oils" />} />
          <Route path="tiny-treasures" element={<TinyTreasuresPage />} />
          <Route path="our-story" element={<StoryPage />} />
          <Route path="product/:slug" element={<ProductDetailPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="my-orders" element={<MyOrdersPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route
            path="admin"
            element={(
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminPage />
              </ProtectedRoute>
            )}
          />
          <Route path="payment/success" element={<PaymentSuccessPage />} />
          <Route path="payment/failure" element={<PaymentFailurePage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ProductsProvider>
          <SiteSettingsProvider>
            <AppRouter />
            <Toaster position="bottom-center" richColors />
          </SiteSettingsProvider>
      </ProductsProvider>
    </BrowserRouter>
  );
}

export default App;
